/**
 * Created by Ning Jiang on 9/20/2017.
 */

import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {ReelSymbolName} from "@nolimitcity/slot-game/bin/core/reelsymbol/ReelSymbolName";
import {IParsedStackedSymbolName, StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {IBetLineWinData, IBetWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {ParsedGameData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedGameData";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {
    InitialWinPresentationController
} from "@nolimitcity/slot-game/bin/game/winpresentation/initial/InitialWinPresentationController";
import {Timeline, TimelineLite} from "gsap";
import {SymbolBurstAnimator} from "../../reelsymbol/SymbolBurstAnimator";
import {WildNudgeReelSymbol} from "../../reelsymbol/WildNudgeReelSymbol";
import {WildNudgeGameConfig} from "../../WildNudgeGameConfig";
import {WildNudgeSoundConfig} from "../../WildNudgeSoundConfig";
import {WildNudgeInitialWinCountUp} from "./WildNudgeInitialWinCountUp";
import {WildNudge} from "../../WildNudge";
import {BelowStakeWinRestriction} from "../../server/BelowStakeWinRestriction";

export class WildNudgeInitialWPController extends InitialWinPresentationController {

    private _symbolWinStates:boolean[][];
    private _symbolWinTexts:string[][][];
    private _winCountUp:WildNudgeInitialWinCountUp;

    private _reelsNum:number;
    private _symbolsNum:number;
    private _belowStakeWinRestriction:boolean;

    constructor(indexes:[number, number]) {
        super(indexes);

        this._reelsNum = GameConfig.instance.REELS_NUM;
        this._symbolsNum = GameConfig.instance.SYMBOLS_NUM_IN_REEL[2];

        this._winCountUp = new WildNudgeInitialWinCountUp();
        // move _winCountUp to a view if needed.
        this._winCountUp.position.set(GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.5);
        StageManager.getLayer(WildNudgeGameConfig.instance.LAYERS.WIN_COUNT_UP.name).addChild(this._winCountUp);
    }

    protected parseFeatureGameData(data:ParsedGameData):void {
        // Calculate no win data. If there is nudged stacked symbol but no win, all its positions will be marked as noWin any way, so no need to handle it specially.
        this._symbolWinStates = ArrayHelper.initArrayWithValues<boolean[]>(this._reelsNum, () => {
            return ArrayHelper.initArrayWithValues<boolean>(this._symbolsNum, () => {return false;})
        });
        this._symbolWinTexts = ArrayHelper.initArrayWithValues<string[][]>(this._reelsNum, () => {
            return ArrayHelper.initArrayWithValues<string[]>(this._symbolsNum, () => {return [];})
        });

        this._betWinsData!.forEach((betWinData:IBetWinData) => {
            const betLineWinData:IBetLineWinData = <IBetLineWinData>betWinData;
            for(let reelId:number = 0; reelId < betLineWinData.nrOfSymbols; reelId++) {
                const symbolId:number = betLineWinData.betLine[reelId];
                this._symbolWinStates[reelId][symbolId] = true;
                // To make all the stack win.
                const parsedStackedSymbolName:IParsedStackedSymbolName = StateReelSymbol.parseStackedSymbolName(this._gameData!.reels[reelId][symbolId + GameConfig.instance.SYMBOLS_NUM_IN_REEL[1]], reelId);
                if(parsedStackedSymbolName.isStacked) {
                    for(let i:number = 0; i < parsedStackedSymbolName.totalNum; i++) {
                        this._symbolWinStates[reelId][Math.min(GameConfig.instance.SYMBOLS_NUM_IN_REEL[2] - 1, Math.max(0, i - parsedStackedSymbolName.index + symbolId))] = true;
                    }
                }
            }
            let winText = WildNudge.api.currency.formatValue(betLineWinData.amount);
            if(betLineWinData.multiplier > 1) {
                winText += `X${betLineWinData.multiplier}`;
            }
            this._symbolWinTexts[betLineWinData.nrOfSymbols - 1][betLineWinData.betLine[betLineWinData.nrOfSymbols - 1]].push(winText);
        });

        this._belowStakeWinRestriction = BelowStakeWinRestriction.isWinBelowPlayedBetValue(data.singleWin, data.playedBetValue);
    }

    protected playAllNoWinSymbols(timeline:Timeline):void {
        this._symbolWinStates.forEach((reelWinStates:boolean[], reelId:number) => {
            reelWinStates.forEach((symbolState:boolean, symbolId:number) => {
                if(!symbolState) {
                    const noWinSymbol:StateReelSymbol | null = Reels.getSymbol(reelId, symbolId);
                    if(noWinSymbol) {
                        timeline.add(noWinSymbol.changeState({
                            state : WildNudgeGameConfig.instance.SYMBOL_STATES.noWin,
                            prioCheck : noWinSymbol.symName === ReelSymbolName.SCATTER,
                            setStack : true,
                            fadeInDuration : 1
                        }), 0);
                    }
                }
            });
        });
    }

    protected addWinAnimationsToTimeline(timeline:Timeline):void {
        let startTime:number = 0;
        for(let i:number = 0; i < GameConfig.instance.REELS_NUM; i++) {
            startTime += this.addReelWinAnimationToTimeline(i, timeline, startTime);
        }

        if(this._gameData!.isBigWin) {
            timeline.add(() => {
                SlotGame.sound.playEffect(WildNudgeSoundConfig.instance.BIG_WIN_START);
                // if abort this, then big win is also aborted, so no need to add double trigger to handle the loop somewhere else.
                SlotGame.sound.fadeAmbience(0, 1500);
            }, 0);
        } // #317
        else if(!this._belowStakeWinRestriction) {
            timeline.add(() => SlotGame.sound.playEffect(WildNudgeSoundConfig.getWinSoundByRatio(this._gameData!.currentWinRatioConfig!.type!)), 0);
        }

        /******  to dim the rows below and above the visible reels  *******/
        let lastIndex:number = GameConfig.instance.SYMBOLS_NUM_IN_REEL[0] + GameConfig.instance.SYMBOLS_NUM_IN_REEL[1]
            + GameConfig.instance.SYMBOLS_NUM_IN_REEL[2] + GameConfig.instance.SYMBOLS_NUM_IN_REEL[3] - 1; // = 8

        let firstIndex:number = GameConfig.instance.SYMBOLS_NUM_IN_REEL[0];

        for(let i:number = 0; i < GameConfig.instance.REELS_NUM; i++) {

            let lastSym:WildNudgeReelSymbol = (<WildNudgeReelSymbol>Reels.getReel(i)!.allSymbols[lastIndex]!);
            let prevToLastSymbol:WildNudgeReelSymbol = (<WildNudgeReelSymbol>Reels.getReel(i)!.allSymbols[lastIndex - 1]!);

            let isLastStacked:boolean = lastSym.symName.charAt(0) === "M";
            let isLastOnSameStacked:boolean = lastSym.symName.substr(0, 2) === prevToLastSymbol.symName.substr(0, 2);
            let isLastSeparateStacked:boolean = lastSym.symName.substr(3, 1) === "0";

            if(!isLastStacked || (isLastStacked && !isLastOnSameStacked) || (isLastStacked && isLastSeparateStacked)) {
                lastSym.changeState({
                    state : WildNudgeGameConfig.instance.SYMBOL_STATES.noWin,
                    prioCheck : true,
                });
            }

            let firstSym:WildNudgeReelSymbol = (<WildNudgeReelSymbol>Reels.getReel(i)!.allSymbols[firstIndex]!);
            let nextToFirstSymbol:WildNudgeReelSymbol = (<WildNudgeReelSymbol>Reels.getReel(i)!.allSymbols[firstIndex + 1]!);

            let isFirstStacked:boolean = firstSym.symName.charAt(0) === "M";
            let isFirstOnSameStacked:boolean = firstSym.symName.substr(0, 2) === nextToFirstSymbol.symName.substr(0, 2);
            let isFirstSeparateStacked:boolean = firstSym.symName.substr(3, 1) === "0";

            if(!isFirstStacked || (isFirstStacked && !isFirstOnSameStacked) || (isFirstStacked && isFirstSeparateStacked)) {
                firstSym.changeState({
                    state : WildNudgeGameConfig.instance.SYMBOL_STATES.noWin,
                    prioCheck : true,
                });
            }
        }
        /*************************  end - dim  **************************/
    }

    private addReelWinAnimationToTimeline(reelId:number, timeline:Timeline, startTime:number):number {
        if(this._belowStakeWinRestriction) {
            return 0.01;
        }
        this._symbolWinStates[reelId].forEach((winState:boolean, symbolId:number) => {
            if(winState) {
                const symbol:StateReelSymbol | null = Reels.getSymbol(reelId, symbolId);
                if(!symbol) {
                    debugger;
                    throw new Error(`Error: WildNudgeInitialWPController.addReelWinAnimationToTimeline(): Cannot find symbol at reel ${reelId} symbol ${symbolId}`);
                }
                if(this._winSymbols.indexOf(symbol) === -1) {
                    this._winSymbols.push(symbol);
                }
                timeline.add(symbol.changeState({
                    state : GameConfig.instance.SYMBOL_STATES.win,
                    fadeInDuration : 0.1,
                    setStack : true,
                    replay : false
                }), startTime);
                this._symbolWinTexts[reelId][symbolId].forEach((winText, i) => {
                    timeline.add(SymbolBurstAnimator.playWinText(reelId, symbolId, winText), startTime + i * 0.2);
                });

                timeline.add(SymbolBurstAnimator.playFireWin(reelId, symbolId), startTime);
            }
        });

        return 0.2;
    }

    protected addWinCountUpToTimeline(timeline:Timeline):void {
        if(this._gameData!.isBigWin) {
            return;
        }

        const totalWin:number = this._gameData!.totalWin;
        const from:number = totalWin - this._gameData!.singleWin;
        const duration:number = this._gameData!.currentWinRatioConfig!.duration;

        const countUpTimeline:TimelineLite = new TimelineLite();
        if(!this._belowStakeWinRestriction) {
            countUpTimeline.add(this._winCountUp.getCountUpAnimation(0, this._gameData!.singleWin, duration, this._gameData!.currentMultiplier));
        }
        countUpTimeline.add(() => SlotGame.winFieldController.showWinField(totalWin));

        timeline.add(countUpTimeline, 0);
    }

    protected stopWinPresentation(isAborted:boolean):void {
        super.stopWinPresentation(isAborted);

        this._symbolWinStates.forEach((reelWinStates:boolean[], reelId:number) => {
            reelWinStates.forEach((symbolState:boolean, symbolId:number) => {
                if(!symbolState) {
                    const noWinSymbol:StateReelSymbol | null = Reels.getSymbol(reelId, symbolId);
                    if(noWinSymbol) {
                        noWinSymbol.changeState({
                            state : WildNudgeGameConfig.instance.SYMBOL_STATES.noWin,
                            prioCheck : noWinSymbol.symName === ReelSymbolName.SCATTER,
                            setStack : true
                        });
                    }
                }
            });
        });

        SymbolBurstAnimator.abortWin();
        if(!this._belowStakeWinRestriction) {
            this._winCountUp.abort(this._gameData!.singleWin, isAborted, this._gameData!.currentMultiplier, this._gameData!.isBigWin);
        }
        if(!this._gameData!.isBigWin) {
            SlotGame.winFieldController.showWinField();
        }
    }
}